// src/components/Groups/utils/simpleDuplicateValidation.ts

import { Student } from "../../../Types/Student";
import { ProcessedStudentWithDuplicates } from "./duplicateDetection";

export interface ValidationResult {
  isValid: boolean;
  conflictsWith?: Student;
  message: string;
  suggestions: string[];
}

/**
 * Simple, clear duplicate validation
 * Rule: Students must have at least ONE different identifying field
 */
export class SimpleDuplicateValidator {
  
  /**
   * Check if a student can be saved (has enough distinguishing information)
   */
  public validateStudent(
    newStudent: ProcessedStudentWithDuplicates,
    existingStudents: Student[]
  ): ValidationResult {
    
    // Find students with same first and last name
    const nameMatches = existingStudents.filter(existing => 
      this.normalizeString(existing.firstName) === this.normalizeString(newStudent.firstName) &&
      this.normalizeString(existing.lastName) === this.normalizeString(newStudent.lastName)
    );

    if (nameMatches.length === 0) {
      return {
        isValid: true,
        message: "✅ Student can be saved - unique name",
        suggestions: []
      };
    }

    // Check each name match to see if we have distinguishing information
    for (const existing of nameMatches) {
      const differences = this.findDifferences(newStudent, existing);
      
      if (differences.length === 0) {
        return {
          isValid: false,
          conflictsWith: existing,
          message: `❌ Cannot save - identical to existing student "${existing.firstName} ${existing.lastName}"`,
          suggestions: [
            "Add a middle name if they have one",
            "Add their date of birth (YYYY-MM-DD)", 
            "Add parent email address",
            "Verify the grade level is correct",
            "Check if this is actually the same student"
          ]
        };
      }
    }

    return {
      isValid: true,
      message: `✅ Student can be saved - distinguishable from ${nameMatches.length} similar student${nameMatches.length > 1 ? 's' : ''}`,
      suggestions: []
    };
  }

  /**
   * Find what's different between two students
   */
  private findDifferences(
    student1: ProcessedStudentWithDuplicates, 
    student2: Student
  ): string[] {
    const differences: string[] = [];

    // Check middle name
    if (student1.middleName && student2.middleName) {
      if (this.normalizeString(student1.middleName) !== this.normalizeString(student2.middleName)) {
        differences.push('middle name');
      }
    } else if (student1.middleName || student2.middleName) {
      differences.push('middle name'); // One has it, other doesn't
    }

    // Check date of birth
    if (student1.dateOfBirth && student2.dateOfBirth) {
      if (student1.dateOfBirth !== student2.dateOfBirth) {
        differences.push('date of birth');
      }
    } else if (student1.dateOfBirth || student2.dateOfBirth) {
      differences.push('date of birth'); // One has it, other doesn't
    }

    // Check grade level
    if (student1.gradeLevel !== student2.gradeLevel) {
      differences.push('grade level');
    }

    // Check parent email
    if (student1.parentEmail && student2.parentEmail) {
      if (this.normalizeString(student1.parentEmail) !== this.normalizeString(student2.parentEmail)) {
        differences.push('parent email');
      }
    } else if (student1.parentEmail || student2.parentEmail) {
      differences.push('parent email'); // One has it, other doesn't
    }

    // Check parent phone
    if (student1.parentPhone && student2.parentPhone) {
      const phone1 = this.normalizePhone(student1.parentPhone);
      const phone2 = this.normalizePhone(student2.parentPhone);
      if (phone1 !== phone2) {
        differences.push('parent phone');
      }
    } else if (student1.parentPhone || student2.parentPhone) {
      differences.push('parent phone'); // One has it, other doesn't
    }

    return differences;
  }

  /**
   * Validate all students in a batch
   */
  public validateStudentBatch(
    students: ProcessedStudentWithDuplicates[],
    existingStudents: Student[]
  ): {
    valid: ProcessedStudentWithDuplicates[];
    invalid: Array<{
      student: ProcessedStudentWithDuplicates;
      validation: ValidationResult;
    }>;
  } {
    const valid: ProcessedStudentWithDuplicates[] = [];
    const invalid: Array<{
      student: ProcessedStudentWithDuplicates;
      validation: ValidationResult;
    }> = [];

    // Also check for duplicates within the new batch
    const allStudentsToCheck = [...existingStudents];

    for (const student of students) {
      const validation = this.validateStudent(student, allStudentsToCheck);
      
      if (validation.isValid) {
        valid.push(student);
        // Add this student to the check list for subsequent students
        allStudentsToCheck.push({
          ...student,
          _id: `temp-${Date.now()}`,
          studentId: `temp-${Date.now()}`,
          teacherId: 'temp'
        } as Student);
      } else {
        invalid.push({ student, validation });
      }
    }

    return { valid, invalid };
  }

  /**
   * Get a display name for a student (for UI purposes)
   */
  public getDisplayName(student: ProcessedStudentWithDuplicates | Student): string {
    let name = `${student.firstName}`;
    
    if (student.middleName) {
      name += ` ${student.middleName}`;
    }
    
    name += ` ${student.lastName}`;
    
    // Add distinguishing info if available
    const extras: string[] = [];
    
    if (student.dateOfBirth) {
      extras.push(`DOB: ${student.dateOfBirth}`);
    }
    
    if (student.gradeLevel) {
      extras.push(`Grade ${student.gradeLevel}`);
    }
    
    if (student.parentEmail) {
      extras.push(`Email: ${student.parentEmail}`);
    }

    if (extras.length > 0) {
      name += ` (${extras.join(', ')})`;
    }

    return name;
  }

  private normalizeString(str: string): string {
    return str.trim().toLowerCase();
  }

  private normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }
}

export const simpleDuplicateValidator = new SimpleDuplicateValidator();
